<template>
  <div v-if="type === 'input'" class="relative flex flex-col">
    <template v-if="isCustomDateTimePreview">
      <div class="date-time-preview-row flex w-full flex-nowrap items-center gap-2">
        <div
          v-if="showDatePreviewInput"
          class="relative min-w-0 flex-1"
          :class="{ 'basis-0': showTimePreviewInput }"
        >
          <CalendarIcon
            :style="{ top: iconTopPosition }"
            :class="`input-icon pointer-events-none absolute left-[12px] z-[1] h-[16px] !w-[16px] -translate-y-1/2`"
          />
          <input
            ref="dateInputElement"
            :value="inputValue"
            :placeholder="datePreviewPlaceholder"
            class="form-placeholder form-control shadow-none focus:outline-transparent focus:ring-0"
            :class="classList"
            :readonly="readonly"
            :style="{
              ...styleObject,
              paddingLeft: '36px',
            }"
            @input="handleInput"
            @focus="handlePreviewFocus"
            @blur="handleBlur"
          />
        </div>
        <div
          v-if="showTimePreviewInput"
          class="relative min-w-0 flex-1"
          :class="{ 'max-w-[180px] basis-0': showDatePreviewInput }"
        >
          <ClockIcon
            :style="{ top: iconTopPosition }"
            :class="`input-icon pointer-events-none absolute left-[12px] z-[1] h-[16px] !w-[16px] -translate-y-1/2`"
          />
          <input
            ref="timeInputElement"
            :value="inputValue"
            :placeholder="timePreviewPlaceholder"
            class="form-placeholder form-control shadow-none focus:outline-transparent focus:ring-0"
            :class="classList"
            :readonly="readonly"
            :style="{
              ...styleObject,
              paddingLeft: '36px',
            }"
            @input="handleInput"
            @focus="handlePreviewFocus"
            @blur="handleBlur"
          />
        </div>
        <UISelect
          v-if="showMeridianSelect"
          class="date-time-meridian-select !w-[112px] !min-w-[112px] !max-w-[112px] shrink-0 self-stretch"
          :value="meridianValue"
          :options="meridianOptions"
          size="large"
        ></UISelect>
      </div>
    </template>
    <template v-else>
      <CalendarIcon
        v-if="element.dataType === 'DATE' || element.dataType === 'DATE_TIME'"
        :style="{ top: iconTopPosition }"
        :class="`input-icon pointer-events-none absolute left-[12px] z-[1] h-[16px] !w-[16px] -translate-y-1/2`"
      />
      <ClockIcon
        v-if="element.dataType === 'TIME'"
        :style="{ top: iconTopPosition }"
        :class="`input-icon pointer-events-none absolute left-[12px] z-[1] h-[16px] !w-[16px] -translate-y-1/2`"
      />
      <Mail01Icon
        v-if="element.dataType === 'EMAIL'"
        :style="{ top: iconTopPosition }"
        :class="`input-icon pointer-events-none absolute left-[12px] z-[1] h-[16px] !w-[16px] -translate-y-1/2`"
      />
      <input
        ref="inputElement"
        :value="inputValue"
        :placeholder="displayPlaceholder"
        class="form-placeholder form-control shadow-none focus:outline-transparent focus:ring-0"
        :class="classList"
        :style="{
          ...styleObject,
          ...(isIconSupported ? { paddingLeft: '36px' } : {}),
        }"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      />
    </template>
    <template v-if="isActive">
      <div
        ref="shortLabelElement"
        :key="resetKey"
        contenteditable="true"
        role="textbox"
        tabindex="0"
        :aria-label="$t('common.shortLabel')"
        class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
        :data-placeholder="$t('common.shortLabelPlaceholder')"
        @blur="updateShortLabel"
        @compositionstart="handleCompositionStart"
        @compositionend="compositionEndHandler"
        @input="handleInputShortLabel"
        @keydown="handleSafariCursor"
        @click="(e) => e.stopPropagation()"
        @focus="(e) => e.stopPropagation()"
        v-text="shortLabel"
      ></div>
    </template>
    <template v-else>
      <span v-if="element.shortLabel" class="short-label font-inter break-words text-sm">
        {{ element.shortLabel }}
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useIMEComposition } from "@/composition/useIMEComposition";
import { useAppStore } from "@/store/app";
import {
  fixSafariCursor,
  handleShortLabelInput as handleShortLabelInputUtil,
} from "@/util/methods";
import {
  buildPreviewToken,
  getDatePreviewPlaceholder,
  getTimePreviewPlaceholder,
  isDateTimeField,
} from "@/util/dateTimeSerialize";
import { CalendarIcon, ClockIcon, Mail01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UISelect } from "@gohighlevel/ghl-ui";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  element: {
    type: Object,
    default: () => {
      return {};
    },
  },
  type: {
    type: String,
    default: "input",
  },
  placeholder: { type: String, default: "" },
  classList: {
    type: Array,
    default: () => {
      return [];
    },
  },
  styleObject: {
    type: Object,
    default: () => {
      return {
        fontSize: "14px",
      };
    },
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
});

const iconTopPosition = ref<string>("50%");
const store = useAppStore();
const { t } = useI18n();
const { handleCompositionStart, handleCompositionEnd, withIMECheck, resetKey } =
  useIMEComposition();
const emit = defineEmits(["update:placeholder", "update:shortLabel"]);
const inputPlaceholder = ref(props.placeholder);
const inputValue = ref("");
const shortLabel = ref(props.element.shortLabel || "");
const shortLabelElement = ref<HTMLElement | null>(null);
const inputElement = ref<HTMLInputElement | null>(null);
const dateInputElement = ref<HTMLInputElement | null>(null);
const timeInputElement = ref<HTMLInputElement | null>(null);

// Handler method to call the imported fixSafariCursor function
const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

const isIconSupported = computed(() => {
  const dataType = props.element.dataType;
  return (
    dataType === "EMAIL" || dataType === "DATE" || dataType === "TIME" || dataType === "DATE_TIME"
  );
});

const isCustomDateTimePreview = computed(() => {
  return isDateTimeField(props.element);
});

const showDatePreviewInput = computed(
  () => props.element.dataType === "DATE" || props.element.dataType === "DATE_TIME"
);
const showTimePreviewInput = computed(
  () => props.element.dataType === "TIME" || props.element.dataType === "DATE_TIME"
);
const showMeridianSelect = computed(
  () => showTimePreviewInput.value && props.element.timeFormat === "12"
);
const meridianValue = computed(() => props.element.customTimeMeridian || "AM");
const meridianOptions = computed(() => [
  { label: t("common.dateTime.meridianAm"), value: "AM" },
  { label: t("common.dateTime.meridianPm"), value: "PM" },
]);
const datePreviewPlaceholder = computed(() => getDatePreviewPlaceholder(props.element));
const timePreviewPlaceholder = computed(() => getTimePreviewPlaceholder(props.element));

// Custom date-time fields preview the composed token; others keep the placeholder.
const displayPlaceholder = computed(() =>
  isCustomDateTimePreview.value
    ? buildPreviewToken(props.element)
    : inputPlaceholder.value
);

watch(
  () => props.placeholder,
  (newVal) => {
    inputPlaceholder.value = newVal;
  }
);

watch(
  () => props.element.shortLabel,
  (newVal) => {
    shortLabel.value = newVal || "";
  }
);

const updateIconPosition = () => {
  nextTick(() => {
    const previewInput = inputElement.value || dateInputElement.value || timeInputElement.value;
    if (previewInput) {
      const inputHeight = previewInput.offsetHeight;
      const centerPosition = inputHeight / 2;
      iconTopPosition.value = `${centerPosition}px`;
    }
  });
};

// Watch for input height changes
watch(
  () => [
    props.styleObject,
    props.classList,
    inputElement.value,
    dateInputElement.value,
    timeInputElement.value,
  ],
  () => {
    updateIconPosition();
  },
  { deep: true }
);

// Watch for isActive changes (when short label appears/disappears)
watch(
  () => props.isActive,
  () => {
    updateIconPosition();
  }
);

onMounted(() => {
  updateIconPosition();
});

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  inputValue.value = target.value;
  inputPlaceholder.value = target.value;
  emit("update:placeholder", inputPlaceholder.value);
};

const handleFocus = (event?: Event) => {
  inputValue.value = inputPlaceholder.value;
  nextTick(() => {
    try {
      const focusedInput =
        event?.target instanceof HTMLInputElement ? event.target : inputElement.value;
      focusedInput?.select?.();
    } catch (error) {
      console.error("Failed to select placeholder text on focus", error);
    }
  });
};

const handlePreviewFocus = () => {
  inputValue.value = "";
};

const handleBlur = (event: Event) => {
  inputValue.value = "";
  store.addToHistory();
};

const updateShortLabel = () => emit("update:shortLabel", shortLabel.value);

const rawInputShortLabelHandler = (e: Event) => {
  handleShortLabelInputUtil(e, shortLabel, updateShortLabel);
};

const handleInputShortLabel = withIMECheck(rawInputShortLabelHandler);
const compositionEndHandler = handleCompositionEnd(rawInputShortLabelHandler);
</script>

<style scoped>
.short-label {
  background-color: transparent;
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}

.date-time-meridian-select :deep(.n-base-selection),
.date-time-meridian-select :deep(.n-base-selection-label) {
  min-height: 100%;
}
</style>
